class SearchTemplate {
	constructor(el, resultsEl) {
		this.el = el;
		this.resultsEl = resultsEl;
		this.categoryFilterData = window.searchTemplateData?.categoryFilters || null;
		this.displaySearchIcon();
		this.initDatePickerFormat();
		this.initClearSearch();
		this.initDependentCategoryFilters();
		this.initResultsCount();
		this.initLiveSearch();
		this.initDateAutoSearch();
	}

	getFilterSelect(key) {
		return this.el.querySelector(`.alm-filter[data-key="${key}"] select`);
	}

	getNormalizedSelectValue(select) {
		if (!select) {
			return '';
		}

		const value = select.value?.trim() || '';

		return value === '#' || value === '0' ? '' : value;
	}

	decodeFilterLabel(label) {
		if (!label || typeof label !== 'string') {
			return label || '';
		}

		const textarea = document.createElement('textarea');
		let decoded = label;
		let previous = '';

		while (decoded !== previous) {
			previous = decoded;
			textarea.innerHTML = decoded;
			decoded = textarea.value;
		}

		return decoded;
	}

	fixSelectOptionLabels(select) {
		if (!select) {
			return;
		}

		Array.from(select.options).forEach((option) => {
			const source = option.dataset.name || option.textContent || '';
			const decoded = this.decodeFilterLabel(source);

			if (!decoded) {
				return;
			}

			option.textContent = decoded;
			option.dataset.name = decoded;
		});
	}

	getSelectedFilterLabel(key) {
		const select = this.getFilterSelect(key);
		const value = this.getNormalizedSelectValue(select);

		if (!select || !value) {
			return '';
		}

		const selectedOption = select.options[select.selectedIndex];

		return selectedOption?.textContent.trim().replace(/^[-\s]+/, '') || '';
	}

	setFilterSelectedValue(key, value) {
		const filter = this.el.querySelector(`.alm-filter[data-key="${key}"]`);

		if (filter) {
			filter.setAttribute('data-selected-value', value);
		}
	}

	renderChildCategoryOptions(parentSlug, selectedChild = '') {
		if (!this.childCategorySelect) {
			return;
		}

		const hasParent = !!parentSlug;
		const placeholderOption = this.childCategorySelect.options[0];
		const placeholderLabel = placeholderOption?.textContent || 'Filter by Sub-category';
		const placeholderValue = placeholderOption?.value || '#';
		const childOptions = this.categoryFilterData?.childrenByParent?.[parentSlug] || [];

		this.childCategorySelect.disabled = !hasParent;
		this.childCategorySelect.setAttribute('aria-disabled', String(!hasParent));

		this.childCategorySelect.innerHTML = '';

		const defaultOption = document.createElement('option');
		defaultOption.value = placeholderValue;
		defaultOption.textContent = placeholderLabel;
		this.childCategorySelect.appendChild(defaultOption);

		if (!hasParent) {
			this.childCategorySelect.value = placeholderValue;
			this.setFilterSelectedValue('category', '');
			return;
		}

		childOptions.forEach((childOption) => {
			const option = document.createElement('option');
			const label = this.decodeFilterLabel(childOption.name);

			option.value = childOption.slug;
			option.textContent = label;
			option.dataset.name = label;

			if (childOption.slug === selectedChild) {
				option.selected = true;
			}

			this.childCategorySelect.appendChild(option);
		});

		const hasSelectedChild = childOptions.some((childOption) => childOption.slug === selectedChild);

		if (!hasSelectedChild) {
			this.childCategorySelect.value = placeholderValue;
			this.setFilterSelectedValue('category', '');
			return;
		}

		this.childCategorySelect.value = selectedChild;
		this.setFilterSelectedValue('category', selectedChild);
	}

	initDependentCategoryFilters() {
		if (!this.categoryFilterData) {
			return;
		}

		this.parentCategorySelect = this.getFilterSelect('taxonomy');
		this.childCategorySelect = this.getFilterSelect('category');

		if (!this.parentCategorySelect || !this.childCategorySelect) {
			return;
		}

		const selectedChild =
			this.getNormalizedSelectValue(this.childCategorySelect) ||
			this.categoryFilterData.selected?.child ||
			'';

		const inferredParent = selectedChild
			? this.categoryFilterData.parentsByChild?.[selectedChild] || ''
			: '';

		const selectedParent =
			this.getNormalizedSelectValue(this.parentCategorySelect) ||
			inferredParent ||
			this.categoryFilterData.selected?.parent ||
			'';

		if (selectedParent) {
			this.parentCategorySelect.value = selectedParent;
			this.setFilterSelectedValue('taxonomy', selectedParent);
		}

		this.renderChildCategoryOptions(selectedParent, selectedChild);
		this.fixSelectOptionLabels(this.parentCategorySelect);
		this.fixSelectOptionLabels(this.childCategorySelect);

		this.el.addEventListener(
			'change',
			(event) => {
				if (event.target !== this.parentCategorySelect) {
					return;
				}

				const parentSlug = this.getNormalizedSelectValue(this.parentCategorySelect);

				this.renderChildCategoryOptions(parentSlug);
			},
			true
		);
	}

	displaySearchIcon() {
		const searchButtons = this.el.querySelectorAll(
			'[data-key="search"] .alm-filter--text-wrap.has-button button'
		);

		searchButtons.forEach((btn) => {
			btn.innerHTML = '<i class="fas fa-search"></i>';
			btn.setAttribute('aria-label', 'Search');
		});

		const otherButtons = this.el.querySelectorAll('.alm-filter--text-wrap.has-button button');

		otherButtons.forEach((btn) => {
			if (!btn.closest('[data-key="search"]')) {
				btn.innerHTML = '';
			}
		});
	}

	initDatePickerFormat() {
		const input = this.el.querySelector('.alm-filter--day input');
		const wrapper = this.el.querySelector('.alm-filter--day .alm-filter--text-wrap');

		if (!input || !wrapper) return;

		input.placeholder = 'Start date    End date';

		let display = wrapper.querySelector('.date-display');
		if (!display) {
			display = document.createElement('div');
			display.className = 'date-display';
			wrapper.appendChild(display);
		}

		const handleChange = () => {
			const realValue = input.value;

			if (!realValue) {
				display.innerHTML = `
                    <span class="date-item is-placeholder">
                        <span class="date-icon"></span>
                        <span>Start Date</span>
                    </span>
                    <span class="date-item is-placeholder">
                        <span class="date-icon"></span>
                        <span>End Date</span>
                    </span>
                `;
				return;
			}

			const parts = realValue.split('|').map((v) => v.trim());
			const start = parts[0];
			const end = parts[1];

			display.innerHTML = `
                <span class="date-item">
	            	<span class="date-icon"></span>
                    <span>${start || 'Start date'}</span>
                </span>
                <span class="date-item">
	            	<span class="date-icon"></span>
                    <span>${end || ' '}</span>
                </span>
		    `;
		};

		input.addEventListener('change', handleChange);
		input.addEventListener('input', handleChange);
		input.addEventListener('blur', handleChange);

		handleChange();
	}

	initClearSearch() {
		const wrapper = this.el.querySelector('[data-key="search"] .alm-filter--text-wrap');
		const input = wrapper?.querySelector('input');

		if (!wrapper || !input) return;

		let clearBtn = wrapper.querySelector('.clear-btn');

		if (!clearBtn) {
			clearBtn = document.createElement('span');
			clearBtn.className = 'clear-btn';
			wrapper.appendChild(clearBtn);
		}

		const toggle = () => {
			clearBtn.classList.toggle('is-visible', input.value.length > 0);
		};

		clearBtn.addEventListener('click', () => {
			input.value = '';

			const filter = this.el.querySelector('[data-key="search"]');
			if (filter) {
				filter.setAttribute('data-selected-value', '');
			}

			this._isTyping = false;
			this._lastInteractionWasInput = true;
			this._shouldRestoreFocus = true;

			const button = this.el.querySelector('[data-key="search"] button');

			if (!button) return;

			clearTimeout(this._liveSearchTimeout);

			button.click();

			input.focus();
		});

		input.addEventListener('input', toggle);

		toggle();
	}

	initResultsCount() {
		const target = this.resultsEl.querySelector('.search-results-count');
		const emptyMessage = this.resultsEl.querySelector('.search-results-empty-message');

		if (!target) {
			return;
		}

		const getSearchTerm = () => {
			const input = document.querySelector('[data-key="search"] input');
			return input?.value.trim() || '';
		};

		const getActiveCategory = () => {
			return this.getSelectedFilterLabel('category') || this.getSelectedFilterLabel('taxonomy');
		};

		const getTotal = () => {
			const alm = document.querySelector('#ajax-load-more');
			if (!alm) return 0;

			return parseInt(alm.getAttribute('data-total-posts'), 10) || 0;
		};

		const update = (source = 'observer') => {
			const total = getTotal();
			const term = getSearchTerm();
			const category = getActiveCategory();
			const label = total === 1 ? 'result' : 'results';
			let text = '';

			if (total === 0) {
				text = term ? `0 results for "${term}"` : '0 results';

				if (category) {
					text += ` in "${category}"`;
				}
			} else {
				text = term ? `${total} ${label} for "${term}"` : `${total} ${label}`;
			}

			target.textContent = text;
			this.resultsEl.classList.toggle('tmpl-search--empty', total === 0);

			if (emptyMessage) {
				emptyMessage.hidden = total !== 0;
			}
		};

		setTimeout(() => update('initial'), 300);
		const alm = document.querySelector('#ajax-load-more');

		if (!alm) {
			return;
		}

		const observer = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (mutation.type === 'attributes') {
					setTimeout(() => {
						update('data-total-posts changed');
					}, 50);
				}
			});
		});

		observer.observe(alm, {
			attributes: true,
			attributeFilter: ['data-total-posts']
		});

		this.el.addEventListener('change', (event) => {
			if (event.target.matches('select')) {
				setTimeout(() => update('select changed'), 50);
			}
		});
	}

	initLiveSearch() {
		if (!this.el) {
			return;
		}

		this._isTyping = false;
		this._shouldRestoreFocus = false;
		this._lastInteractionWasInput = false;

		this.el.addEventListener('input', (e) => {
			if (!e.target.matches('[data-key="search"] input')) return;

			const input = e.target;

			this._isTyping = true;
			this._lastInteractionWasInput = true;
			this._shouldRestoreFocus = true;

			clearTimeout(this._liveSearchTimeout);

			this._liveSearchTimeout = setTimeout(() => {
				const button = this.el.querySelector('[data-key="search"] button');
				if (!button) return;

				this._lastInputValue = input.value;
				button.click();
				this._isTyping = false;
			}, 400);
		});

		this.el.addEventListener('change', (e) => {
			if (e.target.matches('select')) {
				this._lastInteractionWasInput = false;
				this._shouldRestoreFocus = false;
			}
		});

		document.addEventListener('alm_complete', () => {
			if (!this._shouldRestoreFocus) return;

			this.restoreFocus();
		});

		document.addEventListener('focusin', (e) => {
			const isListing = e.target.matches('.alm-listing');
			const isInput = e.target.matches('[data-key="search"] input');
			const isSelect = e.target.matches('select');
			if (isSelect) return;

			if (isInput) return;

			if (isListing && this._shouldRestoreFocus && this._lastInteractionWasInput) {
				this.restoreFocus(true);
			}
		});
	}

	restoreFocus(force = false) {
		const input = this.el.querySelector('[data-key="search"] input');
		if (!input) return;

		const active = document.activeElement;
		if (!force && !this._shouldRestoreFocus) return;

		if (active && active.tagName === 'SELECT') return;

		if (!force && active === input) return;

		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					input.focus();
					const length = input.value.length;
					try {
						input.setSelectionRange(length, length);
					} catch (e) {}
					this._shouldRestoreFocus = false;
					setTimeout(() => {}, 50);
				});
			});
		});
	}

	initDateAutoSearch() {
		const inputs = this.el.querySelectorAll('.alm-flatpickr');
		if (!inputs.length) return;
		inputs.forEach((input) => {
			const waitForFlatpickr = setInterval(() => {
				if (!input._flatpickr) return;
				clearInterval(waitForFlatpickr);
				input._flatpickr.config.onChange.push((selectedDates, dateStr) => {
					const isEmpty = selectedDates.length === 0 || !dateStr;
					const isRangeComplete = selectedDates.length === 2;

					if (!isEmpty && !isRangeComplete) return;

					const button = this.el.querySelector('[data-key="search"] button');

					if (!button) return;

					this._isTyping = false;
					this._lastInteractionWasInput = false;
					this._shouldRestoreFocus = false;

					setTimeout(() => {
						button.click();
					}, 150);
				});
			}, 100);
		});
	}
}

export default SearchTemplate;
